import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Menu,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SearchIcon from "@mui/icons-material/Search";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PrintIcon from "@mui/icons-material/Print";
import { Close, MoreHoriz } from "@mui/icons-material";
import { useAuth } from "../../../context";
import { fetchEndoscopyRecords, fetchEndoscopyTests, updateEndoscopyRecord } from "../../../api/endoscopy";
import { useTranslation } from "react-i18next";

const getNetEndoscopyAmount = (row, test) => {
  const rowAmount = Number(row?.amount);
  if (Number.isFinite(rowAmount)) return rowAmount;
  const baseAmount = Number(test?.price ?? test?.cost ?? 0);
  const discount = Number(row?.discount ?? row?.discount_amount ?? 0);
  if (!Number.isFinite(baseAmount)) return 0;
  return Math.max(0, baseAmount - (Number.isFinite(discount) ? discount : 0));
};

const formatTimeFromRow = (row) => {
  if (row?.record_time) {
    const raw = String(row.record_time).slice(0, 5);
    const [hStr, mStr] = raw.split(":");
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isFinite(h) && Number.isFinite(m)) {
      const period = h >= 12 ? "PM" : "AM";
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
    }
    return raw;
  }
  const raw = row?.created_at || row?.updated_at || null;
  if (!raw) return "-";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
};

const cleanTestTypeName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  return raw
    .replace(/^Endoscopy\s*#?\d*\s*-\s*/i, "")
    .replace(/^Endoscopy\s*/i, "")
    .trim() || "-";
};

const EndoscopyIndex = () => {
  const { t } = useTranslation();
  const [option, setOption] = useState("Daily");
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const token = useAuth()?.user?.token;
  const [rows, setRows] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printHtml, setPrintHtml] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      const [recordData, testData] = await Promise.all([
        fetchEndoscopyRecords(token),
        fetchEndoscopyTests(token),
      ]);
      setRows(Array.isArray(recordData) ? recordData : []);
      setTests(Array.isArray(testData) ? testData : []);
    };
    load();
  }, [token]);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === "Pending" || r.status === "In Review").length,
    [rows]
  );
  const tabFiltered = useMemo(() => {
    const byTab =
      tab === 0
        ? rows.filter((r) => r.status === "Pending" || r.status === "In Review")
        : tab === 1
          ? rows.filter((r) => r.status === "Completed")
          : rows.filter((r) => r.status === "Canceled");
    const q = search.toLowerCase();
    return byTab.filter((r) =>
      [r.ticket_display, r.patient_display?.name, r.test_display?.name, r.status]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, tab, search]);

  const updateTaskStatus = async (id, status) => {
    await updateEndoscopyRecord(token, id, { status });
    const data = await fetchEndoscopyRecords(token);
    setRows(Array.isArray(data) ? data : []);
    setSelectedTask(null);
    setAnchorEl(null);
  };

  const buildPrintTemplate = (row) => {
    const testId = row.test_display?.id || row.test;
    const test = tests.find((t) => String(t.id) === String(testId));
    const amount = getNetEndoscopyAmount(row, test);
    const testName = cleanTestTypeName(row.test_display?.name || test?.name || "-");
    const patientName = row.patient_display?.name || "-";
    const patientId = row.patient_display?.id || "-";
    const doctorName = row.assigned_doctor || "admin";
    const dateText = row.date
      ? new Date(row.date).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";

    const logoUrl = "/logo192.png";
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Endoscopy Result Print</title>
  <style>
    body { font-family: "Segoe UI", Tahoma, Arial, sans-serif; margin: 0; background: #f3f5f7; color: #223; }
    .wrap { max-width: 1080px; margin: 16px auto; background: #fff; box-shadow: 0 10px 30px rgba(0,0,0,.18); border-radius: 4px; overflow: hidden; }
    .top { background: #1f6f8b; color: #fff; padding: 14px 20px 12px; }
    .top-inner { display: grid; grid-template-columns: 92px 1fr 34px; align-items: center; gap: 14px; }
    .logo { width: 82px; height: 82px; border-radius: 6px; background: #fff; object-fit: contain; padding: 4px; }
    .brand { text-align: center; }
    .brand h1 { margin: 0; font-size: 30px; line-height: 1.06; font-weight: 800; }
    .brand h2 { margin: 0; font-size: 20px; line-height: 1.1; font-weight: 700; }
    .brand p { margin: 6px 0 0; font-size: 11px; font-weight: 700; line-height: 1.2; }
    .printer { color: #fff; font-size: 20px; text-align: right; }
    .info { margin: 20px; border: 2px solid #2b7e98; border-radius: 10px; padding: 14px 16px; display: flex; justify-content: space-between; gap: 20px; min-height: 84px; }
    .info p { margin: 8px 0; font-size: 22px; line-height: 1.2; }
    .tbl { margin: 20px; border-radius: 4px; overflow: hidden; border: 1px solid #c8dadd; }
    table { width: 100%; border-collapse: collapse; }
    thead th { background: #1f7894; color: #fff; text-align: left; padding: 14px 12px; font-size: 19px; }
    tbody td { background: #a8d7dc; padding: 14px 12px; font-size: 18px; border-bottom: 1px solid #9ac8cd; }
    .total td { font-weight: 800; font-size: 20px; background: #a8d7dc; }
    .right { text-align: right; }
    .foot { background: #1f6f8b; color: #fff; padding: 16px 20px; font-size: 16px; font-weight: 700; display: flex; justify-content: space-between; }
    @media print { @page { size: A4 portrait; margin: 12mm; } }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="top">
      <div class="top-inner">
        <img class="logo" src="${logoUrl}" alt="Hospital Plus" />
        <div class="brand">
          <h1>Hospital Plus</h1>
          <h2>Hospital Plus</h2>
          <p>Address: Jalalabad city, Public Health, Farhang intersection, opposite the old medicine market</p>
        </div>
        <div class="printer">&#128424;</div>
      </div>
    </div>
    <div class="info">
      <div>
        <p><strong>Patient Name:</strong> ${patientName}</p>
        <p><strong>Patient ID:</strong> ${patientId}</p>
        <p><strong>Requested Date:</strong> ${dateText}</p>
      </div>
      <div>
        <p><strong>Doctor Name:</strong> ${doctorName}</p>
      </div>
    </div>
    <div class="tbl">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Test Name</th>
            <th>Result Value</th>
            <th>Normal Range (Units)</th>
            <th class="right">Cost in (Afghani)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>${testName}</td>
            <td>${row.test_result || "No Result Available"}</td>
            <td>${test?.normal_range || "-"}</td>
            <td class="right">${Number(amount).toFixed(2)} ?</td>
          </tr>
          <tr class="total">
            <td colspan="4">Total Amount</td>
            <td class="right">${Number(amount).toFixed(2)} ?</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="foot">
      <span>| info@afghancosmos.com</span>
      <span>+93 (0) 781 96 81 98</span>
    </div>
  </div>
</body>
</html>`;

    return html;
  };
  const handlePrintTemplate = (row) => {
    const html = buildPrintTemplate(row);
    setPrintHtml(html);
    setPrintPreviewOpen(true);
  };

  const handlePrintFromPreview = () => {
    const printWindow = window.open("", "_blank", "width=1000,height=900");
    if (!printWindow) return;
    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12} md={5} lg={4}>
        <Paper sx={{ p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {t("endoscopy.activityOverview", "Endoscopy Activity Overview")}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t("endoscopy.option", "Option")}</InputLabel>
              <Select
                value={option}
                label={t("endoscopy.option", "Option")}
                onChange={(e) => setOption(e.target.value)}
              >
                <MenuItem value="Daily">{t("endoscopy.daily", "Daily")}</MenuItem>
                <MenuItem value="Weekly">{t("endoscopy.weekly", "Weekly")}</MenuItem>
                <MenuItem value="Monthly">{t("endoscopy.monthly", "Monthly")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: "#1f6f8b", color: "white", borderRadius: 2.5 }}>
                <CardContent sx={{ textAlign: "center", py: 2.5 }}>
                  <EventAvailableIcon sx={{ fontSize: 34, mb: 0.5 }} />
                  <Typography variant="h5" fontWeight={700}>{rows.length}</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {t("endoscopy.totalAppointments", "Total Appointments")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: "#1f6f8b", color: "white", borderRadius: 2.5 }}>
                <CardContent sx={{ textAlign: "center", py: 2.5 }}>
                  <PostAddIcon sx={{ fontSize: 34, mb: 0.5 }} />
                  <Typography variant="h5" fontWeight={700}>{pendingCount}</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {t("endoscopy.queuedPatients", "Queued Patients")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7} lg={8}>
        <Paper sx={{ p: 1 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={t("endoscopy.testToBeDone", "Test To Be Done")} />
            <Tab label={t("endoscopy.done", "Done")} />
            <Tab label={t("endoscopy.canceled", "Canceled")} />
          </Tabs>
          <TextField
            size="small"
            fullWidth
            placeholder={t("endoscopy.searchByTicket", "Search by ticket")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, maxWidth: 420 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("endoscopy.time", "Time")}</TableCell>
                  <TableCell>{t("endoscopy.date", "Date")}</TableCell>
                  <TableCell>{t("endoscopy.patientName", "Patient Name")}</TableCell>
                  <TableCell>{t("endoscopy.doctor", "Doctor")}</TableCell>
                  <TableCell>{t("endoscopy.testType", "Test Type")}</TableCell>
                  <TableCell>{t("endoscopy.amount", "Amount")}</TableCell>
                  <TableCell>{t("endoscopy.paymentStatus", "Payment Status")}</TableCell>
                  <TableCell>{t("endoscopy.status", "Status")}</TableCell>
                  <TableCell>{t("endoscopy.actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabFiltered.map((row) => (
                  <TableRow key={row.id} hover sx={{ bgcolor: "#b7e5ea", "& td": { py: 2.2 } }}>
                    <TableCell>{formatTimeFromRow(row)}</TableCell>
                    <TableCell>
                      {row.date ? new Date(row.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                    </TableCell>
                    <TableCell>{row.patient_display?.name || "-"}</TableCell>
                    <TableCell>{row.assigned_doctor || "admin"}</TableCell>
                    <TableCell>
                      <Box
                        onClick={() => handlePrintTemplate(row)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          color: "#006d77",
                          cursor: "pointer",
                          "&:hover": { opacity: 0.8 },
                        }}
                      >
                        <ListAltIcon fontSize="small" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const testId = row.test_display?.id || row.test;
                        const test = tests.find((t) => String(t.id) === String(testId));
                        const amount = getNetEndoscopyAmount(row, test);
                        return `${amount} ؋`;
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: row.is_paid ? "#1b5e20" : "red", fontWeight: 500 }}>
                        {row.is_paid
                          ? t("endoscopy.paid", "Paid")
                          : t("endoscopy.unpaid", "Unpaid")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            row.status === "Completed"
                              ? "#2e7d32"
                              : row.status === "Canceled"
                                ? "#d32f2f"
                                : "#f9a825",
                          fontWeight: 700,
                        }}
                      >
                        {row.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.status === "Completed" || row.status === "Canceled" ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("endoscopy.noFurtherActions", "No further actions")}
                        </Typography>
                      ) : (
                        <MoreHoriz
                          fontSize="small"
                          style={{ cursor: "pointer", color: "#006d77" }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedTask(row);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl && selectedTask)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedTask(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 300,
            border: "1px solid #d0d0d0",
            borderRadius: 3,
            p: 2,
            boxShadow: 2,
          },
        }}
      >
        {selectedTask && (
          <Box>
            <Box display="flex" justifyContent="flex-end">
              <Close
                sx={{ cursor: "pointer", color: "#7fc8ef" }}
                onClick={() => {
                  setAnchorEl(null);
                  setSelectedTask(null);
                }}
              />
            </Box>
            <Typography variant="h4" sx={{ color: "#13245a", fontWeight: 700, mb: 2 }}>
              {t("endoscopy.task", "Task")}
            </Typography>
            <Typography sx={{ mb: 3 }}>
              {t("endoscopy.status", "Status")}: {selectedTask.status || t("endoscopy.pending", "Pending")}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              sx={{ mb: 2, borderColor: "#f0b000", color: "#f0b000", borderRadius: 2, textTransform: "none" }}
              onClick={() => updateTaskStatus(selectedTask.id, "Completed")}
            >
              {t("endoscopy.markAsCompleted", "Mark as Completed")}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              sx={{ borderColor: "#f0b000", color: "#f0b000", borderRadius: 2, textTransform: "none" }}
              onClick={() => updateTaskStatus(selectedTask.id, "Canceled")}
            >
              {t("endoscopy.markAsCanceled", "Mark as Canceled")}
            </Button>
          </Box>
        )}
      </Menu>
      <Dialog open={printPreviewOpen} onClose={() => setPrintPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: "#f5f5f5" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, bgcolor: "#176d88" }}>
            <IconButton
              onClick={handlePrintFromPreview}
              sx={{ color: "#fff" }}
              title={t("endoscopy.print", "Print")}
            >
              <PrintIcon />
            </IconButton>
            <IconButton
              onClick={() => setPrintPreviewOpen(false)}
              sx={{ color: "#fff" }}
              title={t("endoscopy.close", "Close")}
            >
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ p: 2 }}>
            <iframe
              title="Endoscopy Print Preview"
              style={{ width: "100%", height: "78vh", border: "none", background: "#fff" }}
              srcDoc={printHtml}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default EndoscopyIndex;


